/** GET /api/dose_suggestions — MedKB reference dose for a drug, form, strength and patient. */

export type DoseSuggestionSource = 'mine' | 'guideline' | 'suggested' | 'none'
export type DoseTier = 'guideline' | 'reviewed' | 'ai_consensus' | string

export type DoseSuggestionDetail = {
  indication?: string | null
  population?: string | null
  age_band?: string | null
  citation?: { doc?: string | null; page?: number | string | null } | null
  last_ordered_at?: string | null
  tier?: DoseTier | null
}

/** The MedKB-only answer (regardless of the prescriber's own last order), or null when there is none. */
export type DoseReference = {
  source: Exclude<DoseSuggestionSource, 'mine' | 'none'> | string
  label: string | null
  dose_quantity: string | null
  dose_unit: string | null
  dose_text: string | null
  route: string | null
  frequency_code: string | null
  medication_frequency_id: number | null
  custom_frequency?: unknown
  medication_type?: 'STAT' | 'Routine' | null
  once: boolean | null
  duration_days?: number | null
  /** e.g. "15 mg/kg × 12 kg = 180 mg" for weight-based rows, "180 mg" otherwise. */
  basis: string | null
  max_single_text: string | null
  max_daily_text: string | null
  detail: DoseSuggestionDetail | null
  weight_kg?: number | null
}

/** The top level follows the mine → guideline → suggested → none chain. */
export type DoseSuggestion = DoseReference & {
  source: DoseSuggestionSource
  high_alert: boolean
  medkb_version?: string | null
  reference: DoseReference | null
  /** A weight-based rule applied but the patient has no recent weight on file. */
  weight_needed: boolean
  weight_kg: number | null
}

/** Dosage forms whose MedKB rules do not depend on a strength (mirrors the backend's exempt list). */
export const STRENGTH_EXEMPT_FORMS = [
  'inhaler',
  'aerosol',
  'patch',
  'cream',
  'ointment',
  'gel',
  'lotion',
  'eye drops',
  'nasal spray',
  'suppository',
  'vaginal tablet',
  'pessary'
]

const normalise = (value: string | undefined | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')

/** Whether a reference dose lookup for this form needs the strength to be chosen first. */
export const needsStrength = (form: string | undefined | null) => {
  const key = normalise(form)
  return key !== '' && !STRENGTH_EXEMPT_FORMS.includes(key)
}

/** An empty answer, used when the API says the drug is unknown (422 drug_missing). */
export const NO_DOSE_SUGGESTION: DoseSuggestion = {
  basis: null,
  custom_frequency: null,
  detail: null,
  dose_quantity: null,
  dose_text: null,
  dose_unit: null,
  duration_days: null,
  frequency_code: null,
  high_alert: false,
  label: null,
  max_daily_text: null,
  max_single_text: null,
  medication_frequency_id: null,
  medication_type: null,
  medkb_version: null,
  once: null,
  reference: null,
  route: null,
  source: 'none',
  weight_kg: null,
  weight_needed: false
}

/** Human label for where a reference dose comes from. */
export const doseTierLabel = (reference: Pick<DoseReference, 'source' | 'detail'> | null | undefined) => {
  const tier = reference?.detail?.tier
  if (tier === 'guideline' || tier === 'reviewed') return 'Guideline'
  if (tier === 'ai_consensus') return 'AI-suggested — check'
  return reference?.source === 'suggested' ? 'AI-suggested — check' : 'Guideline'
}
